// =============================================================================
// selectors/accountCards.ts — the Accounts tab's numbers, scope-enforced.
// -----------------------------------------------------------------------------
// `ui/` may not import `domain/` at all, so everything the tab renders comes
// through here. That is the mechanical rule. This file also carries the reason
// the Accounts tab needs a seam of its own rather than a re-export.
//
// ── 🚨 THIS TAB IS THE CLASS-L SCREEN (D47) ──
//
// Not "has a few Class-L numbers on it" — it is made of them. Balance, peak,
// drawdown floor, drawdown buffer, profit-target progress, consistency,
// days-traded and the position-size high-water mark ALL read the whole trade
// history. Under the 45-day boot window every one comes out wrong in the SAME
// direction: a bigger balance, a fuller cushion, a consistency rule that reports
// a false pass on a payout gate.
//
// ⚠ And this is the screen a trader reads mid-session to decide whether there is
// room for another trade. A drawdown buffer that is too generous is precisely
// the number that gets an evaluation blown. So the wrappers take `WholeBook<T>`
// and the caller has to narrow: the block is a compile error, not a review
// comment. `isWholeBook` is re-checked at runtime too, because a cast defeats a
// type and `as WholeBook<T>` is how the compile-time half is actually defeated.
//
// ⚠ The tempting shortcut — show the 45-day figure with an asterisk — is worse
// than showing nothing. A blank with a reason cannot be misread; a wrong number
// with a footnote can.
// =============================================================================

import {
  buildAccountCardModel as buildAccountCardModelImpl,
  buildHouseAggregate as buildHouseAggregateImpl,
  type AccountCardModel, type CardAccount,
} from '../domain/accountCard';
import type { AccountTrade } from '../domain/account';
import type { DayBoundaryMode } from '../domain/range';
import { isWholeBook, type WholeBook } from './population';

export {
  accountLifecycle, accountSignalWeight, barTone, cardFilterCounts,
  cardMatchesFilter, lifecycleLabel, sortAccountCards, FUNDED_TYPES,
} from '../domain/accountCard';
export type {
  AccountCardModel, CardAccount, CardFilter, CardSignal, CardSort,
  GaugeSlot, GaugeState, HouseAggregate, HouseHealth, Lifecycle,
} from '../domain/accountCard';
export type { ConsistencyMath, DrawdownBufferState } from '../domain/account';
export { ruinBand } from '../domain/riskOfRuin';
export type { RuinBand, RuinResult } from '../domain/riskOfRuin';

// The simulator behind the card's one-line figure. Same engine, more of its
// output — `ui/` cannot reach `domain/` even for a type, so it comes through here.
export {
  RUIN_DEFAULT_HORIZON, RUIN_HORIZONS, RUIN_SIM_PATHS, RUIN_THIN_POOL_DAYS,
  dailyPnl, lockParams, seededRandom, simulateRuin,
} from '../domain/riskOfRuin';
export type { RuinSim, RuinSimInput, RuinRules } from '../domain/riskOfRuin';

/**
 * ⚠ The message names the SURFACE and the DIRECTION of the error, deliberately.
 * "Population is not a whole book" tells the next reader nothing they can act
 * on; "this buffer would read larger than the room that exists" tells them the
 * safe response is to block the screen rather than render what loaded.
 */
function requireWholeBook<T>(p: WholeBook<T>, what: string): readonly T[] {
  if (!isWholeBook(p)) {
    throw new Error(
      `${what}: the population is not a whole book. Every number on an account card is a ` +
        `function of the entire trade history — computed from a partial read the buffer reads ` +
        `LARGER than the room that exists, and the card agrees with itself while wrong. ` +
        `Block the surface.`,
    );
  }
  return p.rows;
}

export function buildAccountCardModel<T extends AccountTrade>(
  account: CardAccount,
  population: WholeBook<T>,
  today: string,
  mode: DayBoundaryMode,
): AccountCardModel {
  const rows = requireWholeBook(population, 'buildAccountCardModel');
  return buildAccountCardModelImpl(account, rows, today, mode);
}

/**
 * ⚠ Takes the SAME population as the cards it summarises. Handing the banner one
 * read and the cards another would put two populations on one screen — the exact
 * disagreement this layer exists to prevent, and the old client's own banner
 * already has a version of it (Today's P&L counts SIM accounts, Week P&L does
 * not).
 */
export function buildHouseAggregate<T extends AccountTrade>(
  models: readonly AccountCardModel[],
  population: WholeBook<T>,
  today: string,
  weekStart: string,
  mode: DayBoundaryMode,
) {
  const rows = requireWholeBook(population, 'buildHouseAggregate');
  return buildHouseAggregateImpl(models, rows, today, weekStart, mode);
}
